import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as ctaApi from 'api/sanity/cta';
import { differenceInMonths, isBefore, isFuture, isPast, endOfDay, startOfDay } from 'date-fns';
import { uniqBy } from 'lodash-es';
import { appContextSelector, hasSkippedTour } from 'modules/app/appSlice';
import { lifestyleSubscriptionSelector } from 'modules/subscriptions/subscriptionSlice';
import { co2AmountSelectorMyChooose, co2AmountSelectorPersonal } from 'modules/user/attributionSlice';
import { rgba } from 'polished';
import { getI18n } from 'react-i18next';
import { AppThunk, RootState } from 'store';
import colors from 'themes/default/common/colors';
import { getTypes, hasType } from 'utils/account-type';

function toCtaBox(sanityBox: SanityCtaBox): CtaBox {
  const lang = getI18n().language as Language;
  return {
    _id: sanityBox._id,
    bgColor: rgba(sanityBox.bgColor?.hex ?? '#fff', sanityBox.bgColor?.alpha ?? 1),
    bottomHeaderText: sanityBox.bottomHeaderText?.textContent?.[lang]
      ? sanityBox.bottomHeaderText?.textContent?.[lang]
      : sanityBox.bottomHeaderText?.textContent?.['en'],
    bottomHeaderColor: rgba(sanityBox.bottomHeaderColor?.hex ?? colors.gray[900], sanityBox.bottomHeaderColor?.alpha ?? 1),
    buttonBackgroundColor: rgba(sanityBox.buttonBackgroundColor?.hex ?? colors.pink[500], sanityBox.buttonBackgroundColor?.alpha ?? 1),
    buttonLabel: sanityBox.buttonLabel?.textContent?.[lang] ? sanityBox.buttonLabel.textContent?.[lang] : sanityBox.buttonLabel?.textContent?.['en'],
    buttonLink: sanityBox.buttonLink,
    buttonLinkMode: sanityBox.buttonLinkMode ?? 'same',
    bgImage: sanityBox.bgImage?.asset.url,
    descImage: { url: sanityBox.descImage?.asset?.url ?? '', alt: sanityBox.descImage?.alt ?? '' },
    description: sanityBox.description?.textContent?.[lang] ? sanityBox.description?.textContent?.[lang] : sanityBox.description?.textContent?.['en'],
    descriptionColor: rgba(sanityBox.descriptionColor?.hex ?? colors.gray[900], sanityBox.descriptionColor?.alpha ?? 1),
    topHeaderText: sanityBox.topHeaderText?.textContent?.[lang]
      ? sanityBox.topHeaderText?.textContent?.[lang]
      : sanityBox.topHeaderText?.textContent?.['en'],
    topHeaderColor: rgba(sanityBox.topHeaderColor?.hex ?? colors.gray[900], sanityBox.topHeaderColor?.alpha ?? 1),
    campaignStartDate: sanityBox.campaignStartDate,
    campaignEndDate: sanityBox.campaignEndDate,
    eventStartDate: sanityBox.eventStartDate,
    eventEndDate: sanityBox.eventEndDate,
    buttonTextColor: rgba(sanityBox.buttonTextColor?.hex ?? '#fff', sanityBox.buttonTextColor?.alpha ?? 1),
    dateTextColor: rgba(sanityBox.dateTextColor?.hex ?? colors.gray[900], sanityBox.dateTextColor?.alpha ?? 1),
  };
}

interface State {
  loading: boolean;
  availableBoxes: CtaBox[];
  campaignBoxes: CtaBox[];
  defaultBox?: CtaBox;
  currentCampaignBox?: CtaBox;
  currentGeneralBox?: CtaBox;
}

const initialState: State = {
  loading: false,
  availableBoxes: [],
  campaignBoxes: [],
};

const slice = createSlice({
  name: 'reduce',
  initialState,
  reducers: {
    setAvailableBoxes: (state: State, action: PayloadAction<CtaBox[]>): State => {
      state.availableBoxes = action.payload;
      return state;
    },
    setCampaignBoxes: (state: State, action: PayloadAction<CtaBox[]>): State => {
      state.campaignBoxes = action.payload;
      return state;
    },
    setDefaultBox: (state: State, action: PayloadAction<CtaBox>): State => {
      state.defaultBox = action.payload;
      return state;
    },
    setCurrentCampaignBox: (state: State, action: PayloadAction<CtaBox>): State => {
      state.currentCampaignBox = action.payload;
      return state;
    },
    setCurrentGeneralBox: (state: State, action: PayloadAction<CtaBox>): State => {
      state.currentGeneralBox = action.payload;
      return state;
    },
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.loading = action.payload;
      return state;
    },
  },
});
const { setLoading, setAvailableBoxes, setCurrentCampaignBox, setDefaultBox, setCampaignBoxes, setCurrentGeneralBox } = slice.actions;
export default slice.reducer;

const boxesWithCurrentCampaignDates = (box: CtaBox): boolean =>
  !!box.campaignStartDate &&
  !!box.campaignEndDate &&
  !isFuture(startOfDay(new Date(box.campaignStartDate))) &&
  !isPast(endOfDay(new Date(box.campaignEndDate)));

const boxesByDate = (a: CtaBox, b: CtaBox): number => {
  if (!b.campaignStartDate || !a.campaignStartDate) {
    return 0;
  }
  if (isBefore(new Date(a.campaignStartDate), new Date(b.campaignStartDate))) {
    return 1;
  }
  return -1;
};

export const setCampaignIfAvailable = (items: CtaBox[]): AppThunk => (dispatch): void => {
  const onGoingCampaigns = items.filter(boxesWithCurrentCampaignDates).sort(boxesByDate);

  if (onGoingCampaigns.length) {
    dispatch(setCurrentCampaignBox(onGoingCampaigns[0]));
  }
};

export const setGeneralItemIfAvailable = (items: CtaBox[]): AppThunk => (dispatch): void => {
  const onGoingCampaigns = items.filter(boxesWithCurrentCampaignDates).sort(boxesByDate);

  if (onGoingCampaigns.length) {
    dispatch(setCurrentGeneralBox(onGoingCampaigns[0]));
  }
};

export const loadBoxes = (category: string, context: string): AppThunk => async (dispatch): Promise<void> => {
  dispatch(setLoading(true));

  try {
    const contextData = await ctaApi.getCtaCollection(context);
    const categoryData = await ctaApi.getCtaCollection(category);
    const fallbackBoxData = await ctaApi.getCtaCollection('default-cta-box');
    const generalBoxData = await ctaApi.getCtaCollection('general-cta-box');
    const items = uniqBy(
      (contextData.boxes ?? []).filter(box => (categoryData.boxes ?? []).find(categoryBox => categoryBox._id === box._id)).map(toCtaBox),
      '_id',
    );

    const generalItems = uniqBy((generalBoxData.boxes ?? []).map(toCtaBox), '_id');

    const fallbackItems = uniqBy(
      (contextData.boxes ?? []).filter(box => (fallbackBoxData.boxes ?? []).find(fallbackBox => fallbackBox._id === box._id)).map(toCtaBox),
      '_id',
    );

    const defaultItem = items.find(item => !item.campaignStartDate) ?? fallbackItems.length ? fallbackItems[0] : undefined;

    const campaignItems = items.filter(item => item.campaignStartDate);

    dispatch(setAvailableBoxes(items));
    dispatch(setCampaignIfAvailable(campaignItems));
    dispatch(setGeneralItemIfAvailable(generalItems));
    if (defaultItem) {
      dispatch(setDefaultBox(defaultItem));
    }
    dispatch(setCampaignBoxes(campaignItems));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

type CtaCategoryIdentifier =
  | 'default-cta-box'
  | 'no-subscription-cta-box'
  | 'no-orders-cta-box'
  | 'new-user-cta-box'
  | 'mychooose-with-access-to-wechooose-cta-box'
  | 'new-user-skipped-onboarding-cta-box'
  | 'general-cta-box'
  | 'guest-cta-box';

const ctaContextIdentifierSelector = createSelector(appContextSelector, context => {
  return context.includes('mychooose') ? 'mychooose-cta-context' : 'wechooose-cta-context';
});

const ctaCategorySelector = createSelector(
  appContextSelector,
  lifestyleSubscriptionSelector,
  co2AmountSelectorPersonal,
  co2AmountSelectorMyChooose,
  hasSkippedTour,
  (state: RootState) => state.user,
  (context, sub, co2Personal, co2Mychooose, skippedTour, user): CtaCategoryIdentifier => {
    const isNewUser = differenceInMonths(Date.now(), new Date(user.createdDate ?? '')) <= 1;
    const types = getTypes(user.accounts);
    const carbon = context === 'mychooose' ? co2Mychooose : co2Personal;
    const isMychoooseUserWithWechoooseAccess = hasType(types, ['MyChooose', 'WeChooose']);

    if (['guest-mychooose', 'guest-wechooose'].includes(context)) {
      return 'guest-cta-box';
    }

    if (isNewUser && skippedTour) {
      return 'new-user-skipped-onboarding-cta-box';
    }

    if (isNewUser) {
      return 'new-user-cta-box';
    }

    if (carbon === 0) {
      return 'no-orders-cta-box';
    }

    if (!sub) {
      return 'no-subscription-cta-box';
    }

    if (isMychoooseUserWithWechoooseAccess) {
      return 'mychooose-with-access-to-wechooose-cta-box';
    }

    return 'default-cta-box';
  },
);

export const loadCtaData = (): AppThunk => (dispatch, getState): void => {
  const contextIdentifier = ctaContextIdentifierSelector(getState());
  const categoryIdentifier = ctaCategorySelector(getState());
  dispatch(loadBoxes(categoryIdentifier, contextIdentifier));
};
